"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.Select = void 0;
var _react = _interopRequireWildcard(require("react"));
var _cdsCommon = require("@cbhq/cds-common");
var _useInputVariant = require("@cbhq/cds-common/hooks/useInputVariant");
var _useMergedRef = require("@cbhq/cds-common/hooks/useMergedRef");
var _input = require("@cbhq/cds-common/tokens/input");
var _Dropdown = require("../dropdown/Dropdown");
var _useSpacingValue = require("../hooks/useSpacingValue");
var _HStack = require("../layout/HStack");
var _context = require("./context");
var _SelectTrigger = require("./SelectTrigger");
var _useRefocusTrigger = require("./useRefocusTrigger");
const _excluded = ["children", "value", "valueLabel", "variant", "disabled", "width", "onPress", "helperText", "onChange", "disablePortal"];
function _getRequireWildcardCache(nodeInterop) { if (typeof WeakMap !== "function") return null; var cacheBabelInterop = new WeakMap(); var cacheNodeInterop = new WeakMap(); return (_getRequireWildcardCache = function (nodeInterop) { return nodeInterop ? cacheNodeInterop : cacheBabelInterop; })(nodeInterop); }
function _interopRequireWildcard(obj, nodeInterop) { if (!nodeInterop && obj && obj.__esModule) { return obj; } if (obj === null || typeof obj !== "object" && typeof obj !== "function") { return { default: obj }; } var cache = _getRequireWildcardCache(nodeInterop); if (cache && cache.has(obj)) { return cache.get(obj); } var newObj = {}; var hasPropertyDescriptor = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var key in obj) { if (key !== "default" && Object.prototype.hasOwnProperty.call(obj, key)) { var desc = hasPropertyDescriptor ? Object.getOwnPropertyDescriptor(obj, key) : null; if (desc && (desc.get || desc.set)) { Object.defineProperty(newObj, key, desc); } else { newObj[key] = obj[key]; } } } newObj.default = obj; if (cache) { cache.set(obj, newObj); } return newObj; }
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
const Select = /*#__PURE__*/(0, _react.memo)( /*#__PURE__*/(0, _react.forwardRef)(function Select(_ref, ref) {
  let children = _ref.children,
    value = _ref.value,
    valueLabel = _ref.valueLabel,
    _ref$variant = _ref.variant,
    variant = _ref$variant === void 0 ? 'foregroundMuted' : _ref$variant,
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === void 0 ? false : _ref$disabled,
    _ref$width = _ref.width,
    width = _ref$width === void 0 ? '100%' : _ref$width,
    onPress = _ref.onPress,
    helperText = _ref.helperText,
    onChange = _ref.onChange,
    disablePortal = _ref.disablePortal,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const _useToggler = (0, _cdsCommon.useToggler)(false),
    visible = _useToggler[0],
    toggleDropdownVisibility = _useToggler[1];
  const _useToggler2 = (0, _cdsCommon.useToggler)(false),
    menuHasClosed = _useToggler2[0],
    toggleMenuHasClosed = _useToggler2[1];
  const focusedVariant = (0, _useInputVariant.useInputVariant)(visible, variant);
  /** prevents animation from firing on mount */
  const _useToggler3 = (0, _cdsCommon.useToggler)(false),
    animationsEnabled = _useToggler3[0],
    toggleAnimations = _useToggler3[1];
  /** If the spacer height in InputStack between the helper text and the input changes, this value will need to change */
  const calculateInputStackGap = (0, _useSpacingValue.useSpacingValue)(_input.inputStackGap);
  const menuOffset = calculateInputStackGap + _input.helperTextHeight;
  const triggerRef = (0, _useRefocusTrigger.useRefocusTrigger)(menuHasClosed);
  const handleOnSelectPress = (0, _react.useCallback)(() => {
    onPress === null || onPress === void 0 ? void 0 : onPress();
    toggleAnimations.toggleOn();
  }, [onPress, toggleAnimations]);
  const onOpenMenu = (0, _react.useCallback)(() => {
    toggleDropdownVisibility.toggleOn();
    // this makes sure if you open/close the menu more than once it'll work as expected
    if (menuHasClosed) {
      toggleMenuHasClosed.toggleOff();
    }
  }, [menuHasClosed, toggleDropdownVisibility, toggleMenuHasClosed]);
  const onCloseMenu = (0, _react.useCallback)(() => {
    toggleDropdownVisibility.toggleOff();
    toggleMenuHasClosed.toggleOn();
  }, [toggleDropdownVisibility, toggleMenuHasClosed]);
  const refs = (0, _useMergedRef.useMergedRef)(ref, triggerRef);
  const trigger = (0, _react.useMemo)(() => /*#__PURE__*/_react.default.createElement(_context.TextInputFocusVariantContext.Provider, {
    value: focusedVariant
  }, /*#__PURE__*/_react.default.createElement(_SelectTrigger.SelectTrigger, _extends({
    ref: refs,
    disabled: disabled,
    helperText: helperText,
    onPress: handleOnSelectPress,
    triggerHasFocus: visible,
    value: valueLabel !== null && valueLabel !== void 0 ? valueLabel : value,
    variant: variant,
    visible: animationsEnabled && visible
  }, props))), [focusedVariant, disabled, valueLabel, value, variant, visible, handleOnSelectPress, refs, helperText, animationsEnabled, props]);
  const contentPosition = (0, _react.useMemo)(() => ({
    gap: 0.5,
    offsetGap: helperText ? menuOffset : undefined
  }), [helperText, menuOffset]);
  return /*#__PURE__*/_react.default.createElement(_HStack.HStack, {
    width: width
  }, disabled ? trigger : /*#__PURE__*/_react.default.createElement(_Dropdown.Dropdown, {
    block: true,
    content: children,
    contentPosition: contentPosition,
    disablePortal: disablePortal
    // @ts-expect-error TODO: support string values for dimensions in layout components
    ,
    maxWidth: "95vw",
    onChange: onChange,
    onCloseMenu: onCloseMenu,
    onOpenMenu: onOpenMenu,
    value: value,
    width: width
  }, trigger));
}));
exports.Select = Select;